/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { ChevronDownIconOutline } from '@neo4j-ndl/react/icons';
import classNames from 'classnames';
import type React from 'react';
import { createContext, useContext, useRef, useState } from 'react';

import {
  type CommonProps,
  type HtmlAttributes,
  type PolymorphicCommonProps,
} from '../_common/types';
import { Tooltip } from '../tooltip';

type BreadcrumbsProps = {
  /** Content to be rendered. Should only be `Breadcrumbs.Item`. */
  children?: React.ReactNode;
  /** Additional classnames. */
  shouldWrap?: boolean;
};

const BreadcrumbsComponent = ({
  children,
  className,
  htmlAttributes,
  style,
  ref,
  shouldWrap = true,
  ...restProps
}: CommonProps<'nav', BreadcrumbsProps>) => {
  const classes = classNames('ndl-breadcrumbs', className);
  const listClasses = classNames('ndl-breadcrumbs-list', {
    'ndl-breadcrumbs-wrap': shouldWrap,
  });
  return (
    <nav
      aria-label="Breadcrumb"
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      <ol className={listClasses}>{children}</ol>
    </nav>
  );
};

type BreadcrumbsItemContextType = {
  isInBreadcrumbsItem: boolean;
};

const BreadcrumbsItemContext = createContext<BreadcrumbsItemContextType | null>(
  null,
);

export const useBreadcrumbsItemContext = (): BreadcrumbsItemContextType => {
  const context = useContext(BreadcrumbsItemContext);
  if (!context) {
    throw new Error('This component must be used within a <Breadcrumbs.Item>');
  }

  return context;
};

type BreadcrumbsItemProps = {
  /** Content to be rendered. Should be either `Breadcrumbs.Link`, `Breadcrumbs.Button` or `Breadcrumbs.SelectButton`. */
  children?: React.ReactNode;
};

const BreadcrumbsItem = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'li', BreadcrumbsItemProps>) => {
  const classes = classNames('ndl-breadcrumbs-item', className);

  return (
    <BreadcrumbsItemContext.Provider value={{ isInBreadcrumbsItem: true }}>
      <li
        ref={ref}
        className={classes}
        style={style}
        {...htmlAttributes}
        {...restProps}
      >
        <div className="ndl-breadcrumbs-item-inner">{children}</div>
      </li>
    </BreadcrumbsItemContext.Provider>
  );
};

type InteractiveBreadcrumbProps = {
  /** Content to be rendered. */
  children?: React.ReactNode;
} & (
  | {
      as: 'button';
      /**
       * Event handler for when the button is clicked. Only applicable if `as` is `'button'`.
       */
      onClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      ) => void;
    }
  | {
      as: 'a';
      /**
       * The href of the link. Only applicable if `as` is `'a'`.
       */
      href: HtmlAttributes<'a'>['href'];
      /**
       * The target of the link. Only applicable if `as` is `'a'`.
       */
      target?: HtmlAttributes<'a'>['target'];
    }
  | {
      /**
       * What HTML element to render the root element as.
       */
      as?: React.ElementType;
    }
);

type BreadcrumbsButtonProps = InteractiveBreadcrumbProps & {
  /** Leading element to be rendered. */
  leadingElement?: React.ReactNode;
  /** Trailing element to be rendered. */
  trailingElement?: React.ReactNode;
};

const BreadcrumbsButton = <T extends React.ElementType = 'button'>({
  children,
  as,
  className,
  style,
  htmlAttributes,
  ref,
  leadingElement,
  trailingElement,
  ...restProps
}: PolymorphicCommonProps<T, BreadcrumbsButtonProps>) => {
  useBreadcrumbsItemContext();

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const Component: React.ElementType = as ?? 'button';

  const classes = classNames(
    'ndl-breadcrumbs-button ndl-breadcrumbs-sibling-hover',
    className,
  );

  return (
    <Tooltip
      type="simple"
      isOpen={isTooltipOpen}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          const isTruncated =
            contentRef.current?.scrollWidth &&
            contentRef.current?.clientWidth &&
            contentRef.current.scrollWidth > contentRef.current.clientWidth;
          const isVisible =
            contentRef.current?.offsetWidth &&
            contentRef.current?.offsetHeight &&
            contentRef.current.offsetWidth > 0 &&
            contentRef.current.offsetHeight > 0;
          setIsTooltipOpen(isTruncated || !isVisible);
        } else {
          setIsTooltipOpen(false);
        }
      }}
    >
      <Tooltip.Trigger hasButtonWrapper>
        <Component
          ref={ref}
          className={classes}
          style={style}
          {...htmlAttributes}
          {...restProps}
        >
          {Boolean(leadingElement) && (
            <div className="ndl-breadcrumbs-button-leading-element">
              {leadingElement}
            </div>
          )}
          <div className="ndl-breadcrumbs-button-content" ref={contentRef}>
            {children}
          </div>
          {Boolean(trailingElement) && (
            <div className="ndl-breadcrumbs-button-trailing-element">
              {trailingElement}
            </div>
          )}
        </Component>
      </Tooltip.Trigger>
      <Tooltip.Content>{children}</Tooltip.Content>
    </Tooltip>
  );
};

type BreadcrumbsSelectButtonProps = {
  /** Event handler for when the button is clicked. Should be used to open a `Menu`. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Aria label needed for accessibility. */
  ariaLabel: string;
  /** Content to be rendered. */
  children?: React.ReactNode;
  /** Active - used when a connected menu is open */
  isActive?: boolean;
};

const BreadcrumbsSelectButton = ({
  onClick,
  children,
  isActive,
  className,
  style,
  htmlAttributes,
  ariaLabel,
  ref,
  ...restProps
}: CommonProps<'button', BreadcrumbsSelectButtonProps>) => {
  useBreadcrumbsItemContext();

  const classes = classNames(
    'ndl-breadcrumbs-button ndl-breadcrumbs-sibling-hover ndl-breadcrumbs-select',
    className,
    {
      'ndl-active': Boolean(isActive),
    },
  );

  return (
    <button
      type="button"
      ref={ref}
      className={classes}
      style={style}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={isActive}
      {...htmlAttributes}
      {...restProps}
    >
      <div className="ndl-breadcrumbs-button-leading-element">
        <ChevronDownIconOutline />
      </div>
      <div className="ndl-breadcrumbs-button-content">{children}</div>
    </button>
  );
};

type BreadcrumbsLinkProps = InteractiveBreadcrumbProps & {
  /**
   * Whether the link is the current item. Use `'page'` if it indicates the current page and `'step'` for steps (in a multi-step wizard for example).
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-current
   */
  ariaCurrent?: React.AriaAttributes['aria-current'];
};

const BreadcrumbsLink = <T extends React.ElementType = 'a'>({
  children,
  as,
  className,
  style,
  htmlAttributes,
  ariaCurrent = false,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, BreadcrumbsLinkProps>) => {
  useBreadcrumbsItemContext();

  const Component: React.ElementType = as ?? 'a';

  const classes = classNames(
    'ndl-breadcrumbs-link',
    {
      'ndl-breadcrumbs-link-current': Boolean(ariaCurrent),
    },
    className,
  );

  return (
    <Component
      ref={ref}
      className={classes}
      style={style}
      aria-current={ariaCurrent}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Component>
  );
};

const Breadcrumbs = Object.assign(BreadcrumbsComponent, {
  Button: BreadcrumbsButton,
  Item: BreadcrumbsItem,
  Link: BreadcrumbsLink,
  SelectButton: BreadcrumbsSelectButton,
});

export { Breadcrumbs };
